<template>
    <!--
      조건 트리를 그리는 재귀 컴포넌트. 그룹 안에 그룹이 올 수 있어 자기 자신을 부른다.
      한 그룹에는 접속사(그리고/또는)가 하나뿐이다 — 섞으려면 묶음을 만든다.
      괄호 글자는 그리지 않는다. 묶음은 왼쪽 레일과 들여쓰기로만 보인다.
    -->

    <!-- 조건 하나 -->
    <div v-if="node.kind === 'cond'" class="cond" :class="{active: node.id === active}" :style="colorStyle(node.n)"
         @click.stop="$emit('focus', node.id)">
        <span class="n">{{ prefix }}{{ node.n }}</span>
        <button class="x" title="이 조건 삭제" @click.stop="$emit('del', node.id)">×</button>

        <div class="slot">
            <label>대상</label>
            <div class="target" :class="{empty: !target, active: node.id === active && picking}"
                 @click.stop="$emit('pick', node.id)">
                <template v-if="target">
                    <span class="sw"></span><span>{{ target.say }}</span>
                    <span class="where" :title="target.path">{{ shortPath(target.path) }}</span>
                </template>
                <template v-else>왼쪽 구조에서 대상 고르기 →</template>
            </div>
        </div>

        <template v-if="target">
            <div class="slot">
                <label>비교</label>
                <div class="opts">
                    <span v-for="o in ops" :key="o.k" class="opt" :class="{on: node.op === o.k}"
                          @click.stop="$emit('set', {id: node.id, op: o.k})">{{ o.say }}</span>
                </div>
            </div>
            <div v-if="target.kind === 'choice' || target.kind === 'multi'" class="slot">
                <label>값 — 문구를 보여주고 저장 키를 넣습니다</label>
                <div class="chips">
                    <span v-for="ch in target.choices" :key="ch.value" class="chip" :class="{on: node.value === ch.value}"
                          :title="ch.text" @click.stop="$emit('set', {id: node.id, value: ch.value})">
                        <span class="t">{{ ch.text }}</span>
                        <code v-if="ch.value !== ch.text" class="skey">{{ ch.value }}</code>
                    </span>
                </div>
            </div>
            <div v-else-if="target.kind === 'number'" class="slot">
                <label>값</label>
                <input class="num" type="number" :value="node.value"
                       @input="$emit('set', {id: node.id, value: $event.target.value})" @click.stop>
            </div>
        </template>
    </div>

    <!-- 묶음(그룹) -->
    <div v-else class="grp" :class="{root: isRoot}">
        <div v-if="!isRoot" class="gh">
            묶음 — 안에서는 「{{ node.join === 'and' ? '그리고' : '또는' }}」
            <button class="gx" title="묶음 삭제 (안의 조건도 삭제)" @click.stop="$emit('del', node.id)">×</button>
        </div>

        <template v-for="(it, i) in node.items">
            <div v-if="i > 0" :key="'j' + it.id" class="joiner">
                <span class="line"></span>
                <span class="tog">
                    <button type="button" :class="{on: node.join === 'and'}" @click.stop="$emit('join', {id: node.id, join: 'and'})">그리고</button>
                    <button type="button" :class="{on: node.join === 'or'}" @click.stop="$emit('join', {id: node.id, join: 'or'})">또는</button>
                </span>
                <span class="line"></span>
            </div>
            <sv2-cond :key="it.id" :node="it" :targets="targets" :active="active" :picking="picking" :prefix="prefix" :color="color"
                      @focus="$emit('focus', $event)" @pick="$emit('pick', $event)" @set="$emit('set', $event)"
                      @join="$emit('join', $event)" @add-cond="$emit('add-cond', $event)"
                      @add-group="$emit('add-group', $event)" @del="$emit('del', $event)" />
        </template>

        <div class="gbtns">
            <button class="addc" @click.stop="$emit('add-cond', node.id)">+ 조건</button>
            <button class="addc" @click.stop="$emit('add-group', node.id)">+ 묶음</button>
        </div>
    </div>
</template>

<script>
import {OPS, colorOf} from '@core_utils/subComponent/scoring-formula.js';

const OKLCH = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('color', 'oklch(0.5 0.1 0)');

export default {
    name: 'Sv2Cond',
    props: {
        node: {type: Object, required: true},
        targets: {type: Array, default: () => []},
        active: {type: [Number, String], default: null},
        picking: {type: Boolean, default: false},
        isRoot: {type: Boolean, default: false},
        // 평가요소 안의 조건은 평가요소 색을 함께 쓴다. 없으면 조건 순번으로 계산한다.
        prefix: {type: String, default: ''},
        color: {type: Object, default: null}
    },
    computed: {
        target() { return this.node.kind === 'cond' && this.node.target ? this.targets.find(t => t.id === this.node.target) : null; },
        ops() { return this.target ? (OPS[this.target.kind] || []) : []; }
    },
    methods: {
        colorStyle(n) { const k = this.color || (n ? colorOf(n, OKLCH) : null); return k ? {'--c': k.c, '--ct': k.ct} : {}; },
        shortPath(p) { return p.length > 34 ? p.slice(0, 32) + '…' : p; }
    }
};
</script>

<style scoped>
.cond{border:1px solid #dde2e9;border-left:4px solid var(--c,#dde2e9);border-radius:7px;background:#fbfcfd;
  padding:.7rem .8rem;display:flex;flex-direction:column;gap:.5rem;position:relative;cursor:default}
.cond.active{box-shadow:0 0 0 2px var(--ct,#e6f0f6),0 0 0 3px var(--c,#2f6b8f)}
.cond .n{position:absolute;top:-.6rem;left:.55rem;font-family:Consolas,monospace;font-size:.66rem;font-weight:700;
  background:var(--c,#8b95a3);color:#fff;border-radius:3px;padding:0 .4rem}
.cond .x{position:absolute;top:.35rem;right:.45rem;border:0;background:none;color:#8b95a3;cursor:pointer;
  font-size:1rem;line-height:1;padding:.1rem .3rem;border-radius:3px}
.cond .x:hover{color:#fff;background:#a33228}
.slot{display:flex;flex-direction:column;gap:.25rem}
.slot label{font-size:.72rem;color:#6b747e;font-weight:500}
.target{display:flex;align-items:center;gap:.45rem;min-height:2rem;border:1px dashed #dde2e9;border-radius:5px;
  padding:.3rem .55rem;font-size:.86rem;cursor:pointer;background:#fff}
.target.empty{color:#6b747e}
.target:not(.empty){border-style:solid;border-color:var(--c);background:var(--ct)}
.target.active{border-style:solid;border-color:var(--c,#2f6b8f);background:var(--ct,#e6f0f6)}
.target .sw{width:.7rem;height:.7rem;border-radius:2px;background:var(--c);flex:none}
.target .where{font-size:.7rem;color:#6b747e;font-family:Consolas,monospace;margin-left:auto}
.opts{display:flex;gap:.35rem;flex-wrap:wrap}
.opt{border:1px solid #dde2e9;background:#fff;border-radius:999px;padding:.18rem .65rem;font-size:.8rem;cursor:pointer;color:#3d454e}
.opt:hover{border-color:#2f6b8f;color:#153f47}
.opt.on{border-color:#2f6b8f;background:#2f6b8f;color:#fff}
.chips{display:flex;gap:.35rem;flex-wrap:wrap}
.chip{border:1px solid #dde2e9;background:#fff;border-radius:5px;padding:.22rem .5rem;font-size:.8rem;cursor:pointer;
  display:inline-flex;align-items:center;gap:.3rem;max-width:100%;color:#3d454e}
.chip:hover{border-color:#2f6b8f}
.chip.on{border-color:#2f6b8f;background:#e6f0f6;color:#153f47}
.chip .t{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:14rem}
.skey{font-family:Consolas,monospace;font-size:.68rem;color:#8a6212;background:#fbf3e2;border:1px solid #ecdcb8;
  border-radius:3px;padding:0 .3rem;white-space:nowrap}
input.num{width:7rem;border:1px solid #dde2e9;border-radius:5px;padding:.28rem .5rem;font:inherit;font-size:.86rem;background:#fff}
input.num:focus{outline:2px solid #2f6b8f;outline-offset:-1px}

.grp{border:1px solid #dde2e9;border-left:5px solid #2f6b8f;border-radius:8px;padding:.7rem .7rem .6rem .9rem;
  display:flex;flex-direction:column;gap:.55rem;background:#fff}
.grp.root{border:0;padding:0;background:transparent}
.grp .grp{border-left-color:#1f5f6b}
.grp .gh{display:flex;align-items:center;gap:.5rem;font-size:.72rem;color:#2f6b8f;font-weight:600}
.grp .gh .gx{margin-left:auto;border:0;background:none;color:#8b95a3;cursor:pointer;font-size:1rem;line-height:1;padding:.1rem .3rem;border-radius:3px}
.grp .gh .gx:hover{color:#fff;background:#a33228}
.joiner{display:flex;align-items:center;gap:.5rem;justify-content:center;margin:-.1rem 0}
.joiner .line{flex:1;height:1px;background:#eaeef3}
.joiner .tog{display:inline-flex;border:1px solid #dde2e9;border-radius:999px;overflow:hidden;background:#fff}
.joiner .tog button{border:0;background:none;padding:.18rem .65rem;font:inherit;font-size:.76rem;cursor:pointer;color:#6b747e}
.joiner .tog button.on{background:#2f6b8f;color:#fff}
.gbtns{display:flex;gap:.4rem}
.addc{border:1px dashed #dde2e9;background:none;border-radius:6px;padding:.32rem .7rem;font:inherit;font-size:.8rem;cursor:pointer;color:#2f6b8f}
.addc:hover{border-color:#2f6b8f;background:#e6f0f6}
</style>
